import { useState } from "react";
import { generateAttemptList } from "./CertificationProfileView";
import { useUser } from "../../../../services/user-context";
import { isAdmin, isManager } from "../../../../services/utils";
import { useApp } from "../../../../services/app-context";
import { getLabel } from "../../utils/labels";

export function CertificationTabs({ showManualOption, showSummaryTab, setShowSummaryTab, selectedCertificationAttempt, setSelectedCertificationAttempt, isMe, attemptCount, isInReview }: { showManualOption: boolean, showSummaryTab: boolean, setShowSummaryTab: (value: boolean) => void, selectedCertificationAttempt: string, setSelectedCertificationAttempt: (value: string) => void, isMe: boolean, attemptCount: number, isInReview: boolean }) {
  const { userInfo } = useUser() as any;
  const role = userInfo?.role;
  const { certificationAssets } = useApp();
  const labels = certificationAssets?.labels || {};
  const attemptSingular = getLabel("attempt.singular", labels);
  const sessionSingular = getLabel("session.singular", labels);
  const sessionPlural = getLabel("session.plural", labels);
  const certificationProductName = getLabel("certificationProductName", labels);
  const showManualButton = !showManualOption && (isManager(role) || isAdmin(role)) && !isMe;

  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // as we will show separate tab button when we don't have manual review given, we will send isManager(role) as false.
    const tabs = generateAttemptList(attemptCount, false, showManualOption, labels);
    const onChange = (value: string) => {
        setSelectedCertificationAttempt(value);
        setShowSummaryTab(false);
    }
    const handleSummaryTabClick = () => {
      setShowSummaryTab(!showSummaryTab);
      if (attemptCount > 0) {
        setSelectedCertificationAttempt(String(attemptCount));
      }
    }
    const handleManualButtonClick = () => {
      if (isInReview) {
        setShowBlockedModal(true);
      } else {
        setShowConfirmModal(true);
      }
    };
    const handleConfirmAddManual = () => {
      setShowConfirmModal(false);
      onChange("manual");
    };
    const selectedTab = showSummaryTab ? "summary" : selectedCertificationAttempt;
    return (
        <>
        <div className="flex gap-2 relative pb-2">
            <button
                onClick={handleSummaryTabClick}
                className={`report-tab-button relative z-[5] flex font-semibold uppercase items-center gap-1 text-[clamp(8px,1.2vw,12px)] p-2.5 ${showSummaryTab
                    ? "text-headerPrimary selected-report-tab-button"
                    : "text-text-primary hover:text-text-primary/70"
                    }`}
            >
                Summary
            </button>
            {tabs.map((tab) => (
                <button
                    key={tab.value}
                    onClick={() => onChange(tab.value)}
                    className={`report-tab-button uppercase font-semibold relative z-[5] flex items-center gap-1 text-[clamp(8px,1.2vw,12px)] p-2.5 ${selectedTab === tab.value
                        ? "text-headerPrimary selected-report-tab-button"
                        : "text-text-primary hover:text-text-primary/70"
                        }`}
                >
                    {tab.label}
                </button>
            ))}
            {showManualButton && (
                <button
                    onClick={handleManualButtonClick}
                    className="text-[#ABABAB] text-[clamp(8px,1.2vw,12px)] p-2.5 uppercase font-medium"
                >
                    + Add a manual {attemptSingular.toLowerCase()}
                </button>
            )}
            <div className="absolute bottom-1.5 left-0 w-full h-[1px] bg-gray-402 opacity-50" />
        </div>

        {/* Blocked modal — shown when cert is in review */}
        {showBlockedModal && (
          <>
            <div
              className="fixed top-0 left-0 w-full h-full z-[100] bg-black opacity-30"
              onClick={() => setShowBlockedModal(false)}
            />
            <div className="fixed top-0 left-0 w-full h-full z-[9999] flex items-center justify-center pointer-events-none">
              <div className="bg-white border border-[#e4e4e7] rounded-[22px] p-5 w-[426px] pointer-events-auto flex flex-col gap-5">
                <div>
                  <span className="inline-flex items-center bg-[#f4f4f5] rounded-full px-3 py-1 text-sm font-semibold text-black mb-5">
                    Add Manual {attemptSingular}
                  </span>
                  <p className="text-[20px] font-medium text-black leading-snug mb-5">
                    Complete review before adding a manual {sessionSingular.toLowerCase()}
                  </p>
                  <p className="text-[14px] text-black leading-[1.3]">
                    Before you can add a manual {sessionSingular.toLowerCase()}, please review previous {sessionPlural.toLowerCase()}.
                  </p>
                </div>
                <button
                  onClick={() => setShowBlockedModal(false)}
                  className="w-full bg-red-600 text-white py-2 rounded-[27px] text-[16px]"
                >
                  Close
                </button>
              </div>
            </div>
          </>
        )}

        {/* Confirmation modal — shown when allowed to add a manual session */}
        {showConfirmModal && (
          <>
            <div
              className="fixed top-0 left-0 w-full h-full z-[100] bg-black opacity-30"
              onClick={() => setShowConfirmModal(false)}
            />
            <div className="fixed top-0 left-0 w-full h-full z-[9999] flex items-center justify-center pointer-events-none">
              <div className="bg-white border border-[#e4e4e7] rounded-[22px] p-5 w-[426px] pointer-events-auto flex flex-col gap-5">
                <div>
                  <span className="inline-flex items-center bg-[#f4f4f5] rounded-full px-3 py-1 text-sm font-semibold text-black mb-5">
                    Add Manual {attemptSingular}
                  </span>
                  <p className="text-[20px] font-medium text-black leading-snug mb-5">
                    Confirm adding a manual {sessionSingular.toLowerCase()}
                  </p>
                  <p className="text-[14px] text-black leading-[1.3]">
                    This {certificationProductName} {sessionSingular.toLowerCase()} will not have any AI Insights generated.
                  </p>
                </div>
                <div className="flex gap-5">
                  <button
                    onClick={() => setShowConfirmModal(false)}
                    className="flex-1 border border-black text-black py-2 rounded-full text-[16px]"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmAddManual}
                    className="flex-1 bg-primary3 text-white py-2 rounded-[27px] text-[16px]"
                  >
                    Add Manual {attemptSingular}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
        </>
    );
}